// RITA-1498 (Nutzungserfassung Phase 1b) — Sitzung je Browser-Tab (Konzept 3.1).
// Die Sitzungs-ID liegt im sessionStorage (je Tab, überlebt Reloads, nicht das Schließen).
// Nach IDLE_TIMEOUT_MS ohne Ereignis gilt die Sitzung als beendet; das nächste Ereignis
// eröffnet eine neue Sitzung und meldet die alte mit ihrer letzten Aktivität als beendet.
// Kein Speicherzugriff darf die Bedienung stören: Fehler → Weiterarbeit im Arbeitsspeicher.
export const SESSION_STORAGE_KEY = 'prm.usage.session';
export const IDLE_TIMEOUT_MS = 30 * 60 * 1000;

export interface SessionIdentity {
  session_id: string;
  started_at: number;
}

interface StoredSession extends SessionIdentity {
  last_seen: number;
}

export interface EndedSession {
  session_id: string;
  last_seen: number;
}

export interface SessionState {
  session: SessionIdentity;
  isNew: boolean;
  ended: EndedSession | null;
}

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface CryptoLike {
  randomUUID?: () => string;
  getRandomValues: (array: Uint8Array) => Uint8Array;
}

const defaultCrypto = (): CryptoLike | undefined =>
  typeof globalThis !== 'undefined' ? (globalThis as { crypto?: CryptoLike }).crypto : undefined;

/** UUID v4; crypto.randomUUID braucht einen sicheren Kontext, daher Fallback über getRandomValues. */
export const newSessionId = (cryptoImpl: CryptoLike | undefined = defaultCrypto()): string => {
  if (cryptoImpl && typeof cryptoImpl.randomUUID === 'function') return cryptoImpl.randomUUID();
  let bytes = new Uint8Array(16);
  if (cryptoImpl && typeof cryptoImpl.getRandomValues === 'function') {
    bytes = cryptoImpl.getRandomValues(bytes) || bytes;
  } else {
    bytes = bytes.map(() => Math.floor(Math.random() * 256));
  }
  /* eslint-disable no-bitwise */
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  /* eslint-enable no-bitwise */
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};

export interface SessionStoreDeps {
  storage?: StorageLike | null;
  now?: () => number;
  newId?: () => string;
  idleMs?: number;
}

export interface SessionStore {
  /** Aktuelle Sitzung (zählt als Aktivität); meldet einen Wechsel genau einmal über `ended`/`isNew`. */
  current(): SessionState;
  /** Aktivität ohne Ereignis (z. B. Sichtbarkeitswechsel). */
  touch(): void;
  /** Erzwingt eine neue Sitzung (z. B. Server meldet Sitzungskonflikt 409). */
  reset(): void;
}

const isStoredSession = (value: unknown): value is StoredSession =>
  typeof value === 'object' &&
  value !== null &&
  typeof (value as StoredSession).session_id === 'string' &&
  (value as StoredSession).session_id.length > 0 &&
  Number.isFinite((value as StoredSession).started_at) &&
  Number.isFinite((value as StoredSession).last_seen);

export const createSessionStore = ({
  storage = null,
  now = () => Date.now(),
  newId = () => newSessionId(),
  idleMs = IDLE_TIMEOUT_MS,
}: SessionStoreDeps = {}): SessionStore => {
  let memory: StoredSession | null = null;

  const read = (): StoredSession | null => {
    try {
      const raw = storage ? storage.getItem(SESSION_STORAGE_KEY) : null;
      if (!raw) return memory;
      const parsed: unknown = JSON.parse(raw);
      return isStoredSession(parsed) ? parsed : memory;
    } catch {
      return memory;
    }
  };

  const write = (session: StoredSession): void => {
    memory = session;
    try {
      if (storage) storage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    } catch {
      // Tab-Speicher gesperrt: Arbeitsspeicher genügt.
    }
  };

  const clear = (): void => {
    memory = null;
    try {
      if (storage) storage.removeItem(SESSION_STORAGE_KEY);
    } catch {
      // siehe write
    }
  };

  const toIdentity = (stored: StoredSession): SessionIdentity => ({
    session_id: stored.session_id,
    started_at: stored.started_at,
  });

  return {
    current(): SessionState {
      const at = now();
      const stored = read();
      if (stored && at - stored.last_seen <= idleMs) {
        write({ ...stored, last_seen: at });
        return { session: toIdentity(stored), isNew: false, ended: null };
      }
      const fresh: StoredSession = { session_id: newId(), started_at: at, last_seen: at };
      write(fresh);
      return {
        session: toIdentity(fresh),
        isNew: true,
        ended: stored ? { session_id: stored.session_id, last_seen: stored.last_seen } : null,
      };
    },
    touch(): void {
      const stored = read();
      if (stored) write({ ...stored, last_seen: now() });
    },
    reset(): void {
      clear();
    },
  };
};
